/**
 * The five numbers above the Einheiten list (Units redesign, Slice 4).
 *
 * WHAT CHANGED, AND WHY THE ORDER IS WHAT IT IS. This replaces a row that
 * counted units, apartments, co-living, revenue and margin. Revenue and margin
 * left the top row: they are money, the other three are inventory, and a row
 * that mixes the two invites reading "CHF 5'780" as a property of the same kind
 * of thing as "5 units". Occupancy took their place, which is the question this
 * page is actually opened to answer. Co-Living precedes Business Apartments
 * because it is the larger part of the portfolio.
 *
 * THE COUNTS ARE DERIVED FROM THE EXISTING CONTRACTS, NOT FROM NEW RULES.
 * Segment comes from `getUnitSegment` (Slice 1, which normalises the type the
 * way every domain helper does), occupancy from `getUnitOccupancyStatus`. No
 * second classification lives here.
 *
 * RENTED + VACANT DO NOT HAVE TO EQUAL THE TOTAL, and forcing them to would be
 * the lie. `getUnitOccupancyStatus` answers with five values, and two of them
 * belong in neither card:
 *
 *   belegt      every room / the contract is occupied   -> rented
 *   teilbelegt  some rooms occupied, not all            -> rented (people live there)
 *   frei        nothing occupied, nothing reserved      -> vacant
 *   reserviert  spoken for, nobody has moved in yet     -> NEITHER
 *   null        occupancy not knowable (tenancies not
 *               loaded, or a co-living unit with no
 *               room count)                             -> NEITHER
 *
 * `reserviert` is its own state on purpose: the backend's own unit-level
 * vocabulary keeps `reserved` separate from `occupied` and `vacant`
 * (see PortfolioMapSection), and counting it as either would assert a move-in
 * that has not happened or a vacancy that is already gone. `null` means the
 * answer is unknown, and an unknown counted as "vacant" is a number that
 * invents bad news out of a loading state.
 */
import React from "react";
import { Building2, CheckCircle2, DoorOpen, Home, Users } from "lucide-react";

import {
  getUnitSegment,
  SEGMENT_CO_LIVING,
} from "./unitListModel";
import { getUnitOccupancyStatus } from "../../../utils/unitOccupancyStatus";

/** Occupancy states that mean somebody lives there. */
const RENTED_STATES = new Set(["belegt", "teilbelegt"]);

/** Occupancy states that mean the unit stands empty and unspoken-for. */
const VACANT_STATES = new Set(["frei"]);

/**
 * The five cards, in the order they are shown.
 *
 * Tone and icon are DATA on this list, not a lookup keyed by the visible German
 * label. The row this replaces styled itself with `KPI_VISUAL[label]`, so
 * renaming a card silently dropped it back to the default colour — which is
 * exactly what happens when "Apartments" becomes "Business Apartments".
 */
export const UNIT_KPI_CARDS = [
  {
    key: "total",
    label: "Einheiten gesamt",
    hint: "Alle gefilterten Einheiten",
    icon: Building2,
    tone: "neutral",
  },
  {
    key: "coLiving",
    label: "Co-Living Units",
    hint: "Mehrzimmer-Einheiten",
    icon: Users,
    tone: "info",
  },
  {
    key: "businessApartments",
    label: "Business Apartments",
    hint: "Klassische Einzelwohnungen",
    icon: Home,
    tone: "muted",
  },
  {
    key: "rented",
    label: "Vermietet",
    hint: "Belegt oder teilbelegt",
    icon: CheckCircle2,
    tone: "positive",
  },
  {
    key: "vacant",
    label: "Leerstehend",
    hint: "Ohne Belegung",
    icon: DoorOpen,
    tone: "warning",
  },
];

/**
 * Icon tile tones, same shape as the automation centre's map so the two
 * redesigned surfaces keep one visual vocabulary. Tailwind palette steps with a
 * dark counterpart each — no new hex, and nothing that stops following the
 * theme.
 */
const TONE_CLASSES = {
  neutral: "bg-primary/10 text-primary dark:bg-primary/20",
  info: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300",
  muted: "bg-muted text-muted-foreground dark:bg-white/[0.06]",
  positive:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  // Vacancy is worth noticing, not an error — red is what a failed load looks
  // like on this page, and an empty flat is neither broken nor anybody's fault.
  warning: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
};

/**
 * Count the five figures from the units the page is currently showing.
 *
 * Pure, and exported so the counting can be tested without rendering anything.
 * `rooms` and `tenancies` are the full loaded collections, passed straight
 * through to `getUnitOccupancyStatus` — `tenancies` of null means "not loaded",
 * which that helper answers with null rather than a confident "frei".
 */
export function computeUnitKpiCounts(units, rooms, tenancies) {
  const list = Array.isArray(units) ? units : [];
  const counts = {
    total: list.length,
    coLiving: 0,
    businessApartments: 0,
    rented: 0,
    vacant: 0,
  };

  for (const unit of list) {
    if (getUnitSegment(unit) === SEGMENT_CO_LIVING) counts.coLiving += 1;
    else counts.businessApartments += 1;

    const status = getUnitOccupancyStatus(unit, rooms, tenancies);
    if (RENTED_STATES.has(status)) counts.rented += 1;
    else if (VACANT_STATES.has(status)) counts.vacant += 1;
  }

  return counts;
}

/** A glyph on a tinted square, matching the tile the automation centre uses. */
function KpiIconTile({ icon: Icon, tone }) {
  return (
    <span
      className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] ${
        TONE_CLASSES[tone] || TONE_CLASSES.neutral
      }`}
    >
      <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
    </span>
  );
}

/**
 * One card. Everything visual arrives as a prop — nothing is decided from the
 * label text.
 */
function UnitsKpiCard({ label, value, hint, icon, tone }) {
  return (
    <div className="flex items-center gap-3 rounded-[12px] border border-border bg-card px-4 py-3">
      <KpiIconTile icon={icon} tone={tone} />
      <div className="flex min-w-0 flex-col">
        <dt className="break-words text-[10px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
          {label}
        </dt>
        <dd className="m-0 text-[20px] font-semibold leading-tight text-foreground">
          {value}
        </dd>
        {hint ? (
          <p className="m-0 mt-0.5 text-[10.5px] leading-snug text-muted-foreground">
            {hint}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Five cards across on a wide screen, and never a horizontal scroller.
 *
 * Five columns only from `xl`: "Business Apartments" is nineteen characters
 * beside a 40px tile, and at `lg` five of those share ~190px each and split the
 * word. Three then two reads better than a label broken in half.
 */
export default function UnitsKpiRow({ counts }) {
  if (!counts) return null;

  return (
    <dl className="m-0 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      {UNIT_KPI_CARDS.map((card) => (
        <UnitsKpiCard
          key={card.key}
          label={card.label}
          value={Number(counts[card.key]) || 0}
          hint={card.hint}
          icon={card.icon}
          tone={card.tone}
        />
      ))}
    </dl>
  );
}
